import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createRentalLocation, getRentalLocationById, updateRentalLocation } from 'features/rental-location/actions';
import { checkPermission, mappingLongTimeZones } from 'utils/functionality';
import { getServices } from 'features/services/actions';
import { useAppContext } from 'components/Context/AppContext';
import timeZones from 'utils/time-zones.json';
import timeZonesCode from 'utils/timezone-code.json';
import ServicesForm from './ServicesFormDetail';
import { Button, InputField, SelectFieldSearch, TableWrapper } from 'components/Global';
import Tabs, { TabPane } from 'components/Global/Tabs';

import { ReactComponent as LeftArrow } from 'icons/left-arrow.svg';
import { ReactComponent as LocationFilledIcon } from 'icons/location-filled-icon.svg';
import { getAllCurrencies } from 'features/banks/actions';
import { getConfigsFeaturesLocation } from 'features/configs/actions';
import { useFliptBoolean } from '@flipt-io/flipt-client-react';

const DetailCityPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pathName = location.pathname.split('/').filter((item) => item !== '')[1];

  // All Time Zone
  const allTimeZone = mappingLongTimeZones(timeZones).map((item, idx) => ({ id: idx + 1, name: item }));

  const [searchParams] = useSearchParams();
  const locationId = searchParams.get('location_id');

  const { setShowSpinner, showToast } = useAppContext();

  const { offCanvasMenu, currentMenu } = useSelector((state) => state.menu);
  const { data: dataCurrencies } = useSelector((state) => state.currency);
  const { selected: selectedLocation } = useSelector((state) => state.rentalLocation);
  const { data: serviceData } = useSelector((state) => state.services);

  const [allServices, setAllServices] = useState([]);
  const [mappedCurrencies, setMappedCurrencies] = useState([]);
  const [activeTab, setActiveTab] = useState('With Driver');

  const [cityName, setCityName] = useState('');
  const [timeZoneIdentifier, setTimeZoneIdentifier] = useState(0);
  const [services, setServices] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(0);
  const [isSpecialAirportTransfer, setIsSpecialAirportTransfer] = useState(false);
  const [isMeetAndGreetNotActive, setIsMeetAndGreetNotActive] = useState([]);
  const [isFeeGetAndRideNotActive, setIsFeeGetAndRideNotActive] = useState([]);

  const isCarCharterEnabled = useFliptBoolean('FLAG_CHARTER', false, 'anonymous');

  const getTimezoneCode = (friendlyName) => {
    if (!friendlyName || !timeZonesCode) return;

    const region = friendlyName?.split('/')[0];
    const timeZoneCode = timeZonesCode.timezones[`${region}`][`${friendlyName}`];

    return timeZoneCode;
  };

  // function mutation
  const actionMutation = async (method, payload, successMessage, errorMessage) => {
    if (method === 'ADD') {
      try {
        setShowSpinner(true);
        await dispatch(createRentalLocation(payload)).unwrap();
        showToast({ type: 'success', message: successMessage });
        setShowSpinner(false);
        navigate('/rental-area');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        showToast({ type: 'error', message: error.message ? error.message : errorMessage });
        setShowSpinner(false);
      }
    } else {
      try {
        setShowSpinner(true);
        await dispatch(updateRentalLocation({ id: payload.id, payload: payload.body })).unwrap();
        showToast({ type: 'success', message: successMessage });
        setShowSpinner(false);
        navigate('/rental-area');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        showToast({ type: 'error', message: error.message ? error.message : errorMessage });
        setShowSpinner(false);
      }
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!cityName) {
      showToast({ type: 'error', message: 'Masukan Nama Daerah Sewa' });
      return;
    }

    if (!timeZoneIdentifier) {
      showToast({ type: 'error', message: 'Pilih Time Zone Identifier' });
      return;
    }

    if (!selectedCurrency) {
      showToast({ type: 'error', message: 'Pilih Currency' });
      return;
    }

    if (!services.length) {
      showToast({ type: 'error', message: 'Pilih Service' });
      return;
    }

    if (isMeetAndGreetNotActive.length !== 0) {
      for (let childNotActive of isMeetAndGreetNotActive) {
        if (childNotActive.status) {
          return showToast({
            type: 'error',
            message: `Fitur Meet & Greet belum tersedia untuk layanan ${childNotActive.service}`,
          });
        }
      }
    }

    if (isFeeGetAndRideNotActive.length !== 0) {
      for (let childNotActive of isFeeGetAndRideNotActive) {
        if (childNotActive.status) {
          return showToast({
            type: 'error',
            message: `Fitur Fee Get & Ride belum tersedia untuk layanan ${childNotActive.service}`,
          });
        }
      }
    }

    const payload = {
      name: cityName,
      time_zone_identifier: allTimeZone.find((item) => item.id == timeZoneIdentifier)?.name,
      sub_service_locations: services,
      currency: mappedCurrencies.find((item) => item.id == selectedCurrency)?.name,
      is_special_airport_transfer: isSpecialAirportTransfer,
      time_zone: getTimezoneCode(allTimeZone.find((item) => item.id == timeZoneIdentifier)?.name),
    };

    if (locationId) {
      actionMutation('UPDATE', { id: locationId, body: payload }, 'Berhasil Update Item', 'Gagal Update Item');
    } else {
      actionMutation('ADD', payload, 'Berhasil Tambah Item', 'Gagal Tambah Item');
    }
  };

  useEffect(() => {
    dispatch(getServices());
    dispatch(getAllCurrencies());
    dispatch(getConfigsFeaturesLocation());
  }, []);

  useEffect(() => {
    if (locationId) {
      dispatch(getRentalLocationById(locationId));
    }
  }, [locationId]);

  // Mapping data if selected data doesnt empty
  useEffect(() => {
    let mappingServiceData = [];

    if (Object.keys(selectedLocation).length && locationId && serviceData.length && dataCurrencies.length !== 0) {
      // set time zone identifier
      const selectedTimeZoneIdentifier = allTimeZone.find(
        (item) => item.name === selectedLocation.time_zone_identifier,
      )?.id;

      // set services that checked
      const selectedServices = selectedLocation.sub_services;

      const serviceSewaMobil = serviceData.find((item) => item.name === 'Sewa Mobil');

      const currencySelected = dataCurrencies.data.find((item) => item.symbol === selectedLocation.currency);

      // mapping services
      serviceSewaMobil?.sub_services.forEach((item) => {
        if (item.name !== 'Daily' && item.name !== 'Airport Transfer') return;

        if (item.facilities.length !== 0) {
          for (let facilitiesChild of item.facilities) {
            if (selectedLocation.sub_services?.find((ser) => ser.id === facilitiesChild.id)) {
              mappingServiceData.push({ id: facilitiesChild.id, name: facilitiesChild.name, checked: true });
            } else {
              mappingServiceData.push({ id: facilitiesChild.id, name: facilitiesChild.name, checked: false });
            }
          }
        } else {
          if (selectedLocation?.sub_services.find((ser) => ser.id === item.id)) {
            mappingServiceData.push({ id: item.id, name: item.name, checked: true });
          } else {
            mappingServiceData.push({ id: item.id, name: item.name, checked: false });
          }
        }
      });

      setCityName(selectedLocation.name);
      setTimeZoneIdentifier(selectedTimeZoneIdentifier);
      setServices(selectedServices || []);
      setAllServices(mappingServiceData);
      setSelectedCurrency(currencySelected?.id);
      setIsSpecialAirportTransfer(selectedLocation?.is_special_airport_transfer);
    } else if (serviceData.length) {
      const serviceSewaMobil = serviceData.find((item) => item.name === 'Sewa Mobil');

      serviceSewaMobil?.sub_services.forEach((item) => {
        if (item.name !== 'Daily' && item.name !== 'Airport Transfer') return;

        if (item.facilities.length !== 0) {
          item.facilities.forEach((fac) => mappingServiceData.push({ id: fac.id, name: fac.name, checked: false }));
        } else {
          mappingServiceData.push({ id: item.id, name: item.name, checked: false });
        }
      });
      setAllServices(mappingServiceData);
    }
  }, [selectedLocation, serviceData, dataCurrencies]);

  useEffect(() => {
    if (dataCurrencies.length === 0) return;

    setMappedCurrencies(() =>
      dataCurrencies.data?.map((item) => ({
        id: item.id,
        name: item.symbol,
      })),
    );
  }, [dataCurrencies]);

  const renderTabs = () => {
    const content = [
      { label: 'With Driver', service: 'With Driver' },
      { label: 'Without Driver', service: 'Without Driver' },
      { label: 'Airport Transfer', service: 'Airport Transfer' },
    ];

    if (isCarCharterEnabled) {
      content.push({ label: 'Car Charter', service: 'Charter' });
    }

    return content.map((tab) => (
      <TabPane label={tab.label} key={tab.label}>
        <ServicesForm
          services={services}
          setServices={setServices}
          isSpecialAirportTransfer={isSpecialAirportTransfer}
          setIsSpecialAirportTransfer={setIsSpecialAirportTransfer}
          item={allServices?.find((item) => item.name === tab?.service)}
          setIsMeetAndGreetNotActive={setIsMeetAndGreetNotActive}
          isMeetAndGreetNotActive={isMeetAndGreetNotActive}
          setIsFeeGetAndRideNotActive={setIsFeeGetAndRideNotActive}
          isFeeGetAndRideNotActive={isFeeGetAndRideNotActive}
        />
      </TabPane>
    ));
  };

  return (
    <div className="detail-city">
      <button
        className="detail-city__header"
        onClick={() => navigate(-1)}
        style={{ border: 'none', background: 'transparent' }}
      >
        <LeftArrow />
        <p>Kembali</p>
      </button>

      <TableWrapper
        title={pathName === 'add-item' ? 'Tambah Daerah Sewa' : 'Edit Daerah Sewa'}
        icon={<LocationFilledIcon fill="#009EF7" width="25px" height="25px" />}
      >
        <form className="detail-city__form" onSubmit={submitHandler}>
          <div className="detail-city__form__rent-data">
            <InputField
              label="Nama Daerah Sewa"
              htmlFor="nama-daerah-sewa"
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              placeholder="Nama Daerah Sewa"
            />

            <SelectFieldSearch
              label="Time Zone Identifier"
              placeholder="Pilih Time Zone Identifier"
              htmlFor="time-zone-identifier"
              data={allTimeZone}
              value={timeZoneIdentifier}
              onClick={setTimeZoneIdentifier}
            />

            <SelectFieldSearch
              label="Currency"
              placeholder="Pilih Currency"
              htmlFor="currency"
              data={mappedCurrencies}
              value={selectedCurrency}
              onClick={setSelectedCurrency}
              disable={locationId}
            />
          </div>

          {/* SERVICES */}
          <div className="detail-city__form__services">
            <Tabs activeTab={activeTab} setActiveTab={setActiveTab}>
              {renderTabs()}
            </Tabs>
          </div>

          {(!locationId || checkPermission(offCanvasMenu, currentMenu, 'update')) && (
            <Button width={192} style={{ marginLeft: 'auto' }} role="submit">
              {locationId ? 'Submit' : 'Tambah'}
            </Button>
          )}
        </form>
      </TableWrapper>
    </div>
  );
};

export default DetailCityPage;
